import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faTableList, faMagnifyingGlass, faXmark, faSort, faSortUp, faSortDown,
    faEye, faChevronLeft, faChevronRight, faAnglesLeft, faAnglesRight,
    faSpinner, faDownload, faBoxesStacked, faWeightHanging, faCube,
    faDollarSign, faPercent, faCheck, faUsers, faSliders,
} from "@fortawesome/free-solid-svg-icons";
import { fetchWithAuth } from "../../utils/apiClient";

// ─── Column definitions ──────────────────────────────────────────────────────

const ALL_COLUMNS = [
    { key: "despatch_date", label: "Despatch Date" },
    { key: "machship_number", label: "Machship #" },
    { key: "carrier_number", label: "Carrier #" },
    { key: "carrier_name", label: "Carrier" },
    { key: "carrier_service_name", label: "Service" },
    { key: "customer_name", label: "Customer" },
    { key: "customer_management_status", label: "Management Status" },
    { key: "tracking_status", label: "Tracking Status" },
    { key: "from_suburb", label: "From Suburb" },
    { key: "from_state", label: "From State" },
    { key: "to_suburb", label: "To Suburb" },
    { key: "to_state", label: "To State" },
    { key: "metro_regional", label: "Metro / Regional" },
    { key: "total_quantity", label: "Qty" },
    { key: "total_weight_kg", label: "Weight (kg)" },
    { key: "total_cubic_volume", label: "Volume (m³)" },
    { key: "distance", label: "Distance (km)" },
    { key: "machship_eta", label: "Machship ETA" },
    { key: "carrier_eta_integration", label: "Carrier ETA" },
    { key: "user_updated_eta", label: "User Updated ETA" },
    { key: "reconciled_eta", label: "Reconciled ETA" },
    { key: "machship_completed_date", label: "Machship Completed" },
    { key: "carrier_completed_date", label: "Carrier Completed" },
    { key: "user_updated_completed_date", label: "User Completed" },
    { key: "reconciled_completed_date", label: "Reconciled Completed" },
    { key: "kpi_measure", label: "KPI Measure" },
    { key: "kpi_measure_old", label: "KPI Measure (OLD)" },
    { key: "kpi_hit", label: "KPI Hit" },
    { key: "kpi_explanation", label: "KPI Explanation" },
    { key: "is_processed", label: "Processed" },
    { key: "processed_by", label: "Processed By" },
    { key: "processed_date", label: "Processed Date" },
    { key: "process_steps", label: "Process Steps" },
    { key: "has_attachment", label: "Has Attachment" },
    { key: "eta_affected_by_public_holidays", label: "ETA (Public Holidays)" },
    { key: "mcshp_base_cost", label: "MCSHP Base Cost" },
    { key: "mcshp_fuel_cost", label: "MCSHP Fuel Cost" },
    { key: "mcshp_net_cost_ex_tax", label: "MCSHP Net Cost (Ex Tax)" },
    { key: "mcshp_tax_cost", label: "MCSHP Tax Cost" },
    { key: "mcshp_total_cost_inc_tax", label: "MCSHP Total Cost (Inc Tax)" },
    { key: "mcshp_base_charge", label: "MCSHP Base Charge" },
    { key: "mcshp_fuel_charge", label: "MCSHP Fuel Charge" },
    { key: "mcshp_net_charge_ex_tax", label: "MCSHP Net Charge (Ex Tax)" },
    { key: "mcshp_tax_charge", label: "MCSHP Tax Charge" },
    { key: "mcshp_total_charge_inc_tax", label: "MCSHP Total Charge (Inc Tax)" },
    { key: "reconciled_base_cost", label: "Reconciled Base Cost" },
    { key: "reconciled_fuel_cost", label: "Reconciled Fuel Cost" },
    { key: "reconciled_net_cost_ex_tax", label: "Reconciled Net Cost (Ex Tax)" },
    { key: "reconciled_tax_cost", label: "Reconciled Tax Cost" },
    { key: "reconciled_total_cost_inc_tax", label: "Reconciled Total Cost (Inc Tax)" },
    { key: "reconciled_base_charge", label: "Reconciled Base Charge" },
    { key: "reconciled_fuel_charge", label: "Reconciled Fuel Charge" },
    { key: "reconciled_net_charge_ex_tax", label: "Reconciled Net Charge (Ex Tax)" },
    { key: "reconciled_tax_charge", label: "Reconciled Tax Charge" },
    { key: "reconciled_total_charge_inc_tax", label: "Reconciled Total Charge (Inc Tax)" },
    { key: "total_base_cost_price", label: "Base Cost" },
    { key: "cost_fuel_levy_price", label: "Cost Fuel Levy" },
    { key: "total_base_sell_price", label: "Base Sell" },
    { key: "sell_fuel_levy_price", label: "Sell Fuel Levy" },
    { key: "pre_surcharge", label: "Pre Surcharge" },
    { key: "post_surcharge", label: "Post Surcharge" },
    { key: "total_base_sell_price_raw", label: "Base Sell (Raw)" },
    { key: "total_fuel_sell", label: "Fuel Sell" },
    { key: "total_tax_sell_price", label: "Tax Sell Price" },
    { key: "total_sell_price", label: "Total Sell Price" },
    { key: "reconciled_base_cost_price", label: "Reconciled Base Cost" },
    { key: "reconciled_fuel_levy_price", label: "Reconciled Fuel Levy" },
    { key: "reconciled_total_cost_price", label: "Reconciled Total Cost" },
    { key: "is_reconciled", label: "Reconciled" },
    { key: "from_name", label: "From Name" },
    { key: "from_postcode", label: "From Postcode" },
    { key: "to_name", label: "To Name" },
    { key: "to_postcode", label: "To Postcode" },
    { key: "items", label: "Items" },
    { key: "max_item_height_cm", label: "Max Height (cm)" },
    { key: "max_item_length_cm", label: "Max Length (cm)" },
    { key: "max_item_width_cm", label: "Max Width (cm)" },
    { key: "special_instructions", label: "Special Instructions" },
    { key: "is_dg_consignment", label: "DG Consignment" },
    { key: "carrier_dispatch_date", label: "Carrier Dispatch Date" },
    { key: "reconciled_dispatch_date", label: "Reconciled Dispatch Date" },
    { key: "manifested_date", label: "Manifested Date" },
    { key: "date_created", label: "Date Created" },
    { key: "accepted_total_ex_tax", label: "Accepted Total (Ex GST)" },
    { key: "accepted_reasons", label: "Accepted Reasons" },
    { key: "rejected_total_ex_tax", label: "Rejected Total (Ex GST)" },
    { key: "rejected_reasons", label: "Rejected Reasons" },
    { key: "absorbed_total_ex_tax", label: "Absorbed Total (Ex GST)" },
    { key: "absorbed_reasons", label: "Absorbed Reasons" },
    { key: "manual_charges_total_ex_tax", label: "Manual Charges Total (Ex GST)" },
    { key: "manual_charges_reasons", label: "Manual Charges Reasons" },
    { key: "credit_total_ex_tax", label: "Credit Total (Ex GST)" },
    { key: "credit_reasons", label: "Credit Reasons" },
    { key: "carrier_invoice", label: "Carrier Invoice" },
    { key: "finance_invoice", label: "Finance Invoice" },
];

const PRESETS = {
    Core: ["despatch_date","machship_number","carrier_number","carrier_name","carrier_service_name","customer_name","customer_management_status","tracking_status","from_suburb","from_state","to_suburb","to_state","metro_regional","total_quantity","total_weight_kg","total_cubic_volume"],
    "DIFOT/KPI": ["despatch_date","machship_number","carrier_name","customer_name","machship_eta","carrier_eta_integration","user_updated_eta","reconciled_eta","machship_completed_date","carrier_completed_date","user_updated_completed_date","reconciled_completed_date","kpi_measure","kpi_measure_old","kpi_hit","kpi_explanation","is_processed","processed_by","processed_date","process_steps","has_attachment","eta_affected_by_public_holidays"],
    Financial: ["despatch_date","machship_number","carrier_name","customer_name","mcshp_base_cost","mcshp_fuel_cost","mcshp_net_cost_ex_tax","mcshp_tax_cost","mcshp_total_cost_inc_tax","mcshp_base_charge","mcshp_fuel_charge","mcshp_net_charge_ex_tax","mcshp_tax_charge","mcshp_total_charge_inc_tax","reconciled_base_cost","reconciled_fuel_cost","reconciled_net_cost_ex_tax","reconciled_tax_cost","reconciled_total_cost_inc_tax","reconciled_base_charge","reconciled_fuel_charge","reconciled_net_charge_ex_tax","reconciled_tax_charge","reconciled_total_charge_inc_tax","total_base_cost_price","cost_fuel_levy_price","total_base_sell_price","sell_fuel_levy_price","pre_surcharge","post_surcharge","total_base_sell_price_raw","total_fuel_sell","total_sell_price","total_tax_sell_price","reconciled_base_cost_price","reconciled_fuel_levy_price","reconciled_total_cost_price","is_reconciled"],
    Logistics: ["despatch_date","machship_number","carrier_name","carrier_service_name","from_name","from_suburb","from_state","from_postcode","to_name","to_suburb","to_state","to_postcode","metro_regional","total_quantity","total_weight_kg","total_cubic_volume","distance","items","max_item_height_cm","max_item_length_cm","max_item_width_cm","special_instructions","is_dg_consignment"],
    Dates: ["despatch_date","carrier_dispatch_date","reconciled_dispatch_date","machship_eta","carrier_eta_integration","user_updated_eta","reconciled_eta","machship_completed_date","carrier_completed_date","user_updated_completed_date","reconciled_completed_date","manifested_date","date_created"],
    Surcharges: ["despatch_date","machship_number","carrier_name","customer_name","accepted_total_ex_tax","accepted_reasons","rejected_total_ex_tax","rejected_reasons","absorbed_total_ex_tax","absorbed_reasons","manual_charges_total_ex_tax","manual_charges_reasons","credit_total_ex_tax","credit_reasons","carrier_invoice","finance_invoice"],
    "All Columns": ALL_COLUMNS.map((c) => c.key),
};

const PRESET_NAMES = ["Core", "DIFOT/KPI", "Financial", "Surcharges", "Logistics", "Dates", "All Columns"];
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

// ─── Formatting ──────────────────────────────────────────────────────────────

const CURRENCY_KEYS = new Set([
    "accepted_total_ex_tax",
    "rejected_total_ex_tax",
    "absorbed_total_ex_tax",
    "manual_charges_total_ex_tax",
    "credit_total_ex_tax",
    "mcshp_base_cost",
    "mcshp_fuel_cost",
    "mcshp_net_cost_ex_tax",
    "mcshp_tax_cost",
    "mcshp_total_cost_inc_tax",
    "mcshp_base_charge",
    "mcshp_fuel_charge",
    "mcshp_net_charge_ex_tax",
    "mcshp_tax_charge",
    "mcshp_total_charge_inc_tax",
    "reconciled_base_cost",
    "reconciled_fuel_cost",
    "reconciled_net_cost_ex_tax",
    "reconciled_tax_cost",
    "reconciled_total_cost_inc_tax",
    "reconciled_base_charge",
    "reconciled_fuel_charge",
    "reconciled_net_charge_ex_tax",
    "reconciled_tax_charge",
    "reconciled_total_charge_inc_tax",
    "total_base_cost_price",
    "cost_fuel_levy_price",
    "total_base_sell_price",
    "sell_fuel_levy_price",
    "pre_surcharge",
    "post_surcharge",
    "total_base_sell_price_raw",
    "total_fuel_sell",
    "total_sell_price",
    "total_tax_sell_price",
    "reconciled_base_cost_price",
    "reconciled_fuel_levy_price",
    "reconciled_total_cost_price",
]);
const BOOL_KEYS = new Set(["is_processed","is_reconciled","is_dg_consignment","has_attachment","eta_affected_by_public_holidays","kpi_hit"]);
const DATE_KEYS = new Set(["despatch_date","machship_eta","carrier_eta_integration","user_updated_eta","reconciled_eta","machship_completed_date","carrier_completed_date","user_updated_completed_date","reconciled_completed_date","manifested_date","date_created","processed_date","carrier_dispatch_date","reconciled_dispatch_date"]);

function formatMetricValue(key, value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return String(value);
    if (key === "total_weight_kg") {
        return numeric.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (key === "total_cubic_volume") {
        return numeric.toLocaleString("en-AU", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    }
    if (key === "distance") {
        return numeric.toLocaleString("en-AU", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    }
    return String(value);
}

function formatBooleanValue(value) {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    const normalized = String(value ?? "").trim().toLowerCase();
    if (["1", "true", "yes", "y"].includes(normalized)) return "Yes";
    if (["0", "false", "no", "n", ""].includes(normalized)) return "No";
    return String(value);
}

function formatCell(key, value) {
    if (value === null || value === undefined || value === "") return "—";
    if (BOOL_KEYS.has(key)) return formatBooleanValue(value);
    if (CURRENCY_KEYS.has(key)) return `$${Number(value).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (DATE_KEYS.has(key)) {
        try { return new Date(value).toLocaleDateString("en-AU"); } catch { return String(value); }
    }
    if (key === "total_weight_kg" || key === "total_cubic_volume" || key === "distance") return formatMetricValue(key, value);
    return String(value);
}

const normalizeRows = (rows) =>
    (rows || []).map((row) => {
        const normalized = {};
        Object.entries(row || {}).forEach(([key, value]) => {
            normalized[key] = value;
            const lowerKey = key.toLowerCase();
            if (!(lowerKey in normalized)) normalized[lowerKey] = value;
        });
        return normalized;
    });

// ─── KPI Strip ───────────────────────────────────────────────────────────────

function KPIStrip({ summary }) {
    const {
        total_consignments, total_weight_kg, total_cubic_volume,
        margin, margin_pct,
        reconciled_total_charge_inc_tax, reconciled_total_cost_inc_tax,
        processed_count, unique_carriers, unique_customers,
    } = summary;

    const totalCharge = reconciled_total_charge_inc_tax;
    const totalCost = reconciled_total_cost_inc_tax;
    const pos = Number(margin) >= 0;
    const avgW = total_consignments > 0 ? (total_weight_kg / total_consignments).toFixed(1) : "0.0";

    const tiles = [
        { label: "Consignments", icon: faBoxesStacked, color: "#3D7AB5", bg: "#EBF3FB",
          value: Number(total_consignments).toLocaleString() },
        { label: "Total Weight", icon: faWeightHanging, color: "#E8913A", bg: "#FEF3E8",
          value: `${Number(total_weight_kg).toLocaleString("en-AU", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg`,
          sub: `${avgW} kg avg` },
        { label: "Volume", icon: faCube, color: "#3D7AB5", bg: "#EBF3FB",
          value: `${Number(total_cubic_volume).toLocaleString("en-AU", { minimumFractionDigits: 4, maximumFractionDigits: 4 })} m³` },
        { label: "Reconciled Charge", icon: faDollarSign, color: "#2D8659", bg: "#E8F5EE",
          value: `$${Number(totalCharge).toLocaleString("en-AU", { maximumFractionDigits: 0 })}` },
        { label: "Reconciled Cost", icon: faDollarSign, color: "#E8913A", bg: "#FEF3E8",
          value: `$${Number(totalCost).toLocaleString("en-AU", { maximumFractionDigits: 0 })}` },
        { label: "Margin", icon: faPercent, color: pos ? "#2D8659" : "#C0392B", bg: pos ? "#E8F5EE" : "#FDECEA",
          value: `$${Number(Math.abs(margin)).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`,
          sub: `${Number(margin_pct).toFixed(1)}%`, subColor: pos ? "#2D8659" : "#C0392B" },
        { label: "Processed", icon: faCheck, color: "#2D8659", bg: "#E8F5EE",
          value: Number(processed_count).toLocaleString(),
          sub: `of ${Number(total_consignments).toLocaleString()}` },
        { label: "Carriers / Customers", icon: faUsers, color: "#3D7AB5", bg: "#EBF3FB",
          value: `${Number(unique_carriers).toLocaleString()} / ${Number(unique_customers).toLocaleString()}` },
    ];

    return (
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3 px-6 pb-4">
            {tiles.map((t) => (
                <div key={t.label} className="bg-white rounded-xl ring-1 ring-gray-200/60 p-3.5 flex flex-col gap-0.5 relative overflow-hidden"
                    style={{ borderLeft: `3px solid ${t.color}` }}>
                    <div className="absolute top-2.5 right-2.5 w-6 h-6 rounded-lg flex items-center justify-center" style={{ backgroundColor: t.bg }}>
                        <FontAwesomeIcon icon={t.icon} className="w-3 h-3" style={{ color: t.color }} />
                    </div>
                    <p className="text-[10px] font-semibold text-gray-400 pr-7 leading-tight uppercase tracking-wide">{t.label}</p>
                    <p className="text-sm font-bold text-[#144677] leading-snug">{t.value}</p>
                    {t.sub && <p className="text-xs font-semibold" style={{ color: t.subColor || "#9ca3af" }}>{t.sub}</p>}
                </div>
            ))}
        </div>
    );
}

// ─── Column picker ───────────────────────────────────────────────────────────

function ColPicker({ visibleCols, onToggle, onPreset, activePreset }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? ALL_COLUMNS.filter((c) => c.label.toLowerCase().includes(q)) : ALL_COLUMNS;
    }, [search]);

    return (
        <div className="relative" ref={ref}>
            <button onClick={() => setOpen((p) => !p)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border rounded-lg transition-colors ${open ? "bg-[#144677] text-white border-[#144677]" : "text-gray-600 border-gray-200 hover:border-[#144677]/50"}`}>
                <FontAwesomeIcon icon={faEye} className="w-3 h-3" />
                Columns ({visibleCols.size})
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-xl shadow-xl ring-1 ring-gray-200 z-50 overflow-hidden">
                    <div className="px-3 pt-2 pb-1.5 border-b border-gray-100">
                        <div className="relative">
                            <FontAwesomeIcon icon={faMagnifyingGlass} className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                            <input type="text" placeholder="Search columns…" value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full pl-6 pr-5 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#144677]" />
                            {search && (
                                <button onClick={() => setSearch("")} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    <FontAwesomeIcon icon={faXmark} className="w-3 h-3" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto py-1">
                        {filtered.map((col) => (
                            <label key={col.key} className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-gray-50 cursor-pointer">
                                <input type="checkbox" checked={visibleCols.has(col.key)} onChange={() => onToggle(col.key)}
                                    className="w-3.5 h-3.5 accent-[#144677] flex-shrink-0" />
                                <span className={`text-xs truncate ${visibleCols.has(col.key) ? "text-gray-700" : "text-gray-400 line-through"}`}>{col.label}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function Pagination({ currentPage, totalPages, isLoading, onPageChange, pageSize, totalRows, onPageSizeChange }) {
    const pages = useMemo(() => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        const result = [1];
        const left = Math.max(2, currentPage - 2);
        const right = Math.min(totalPages - 1, currentPage + 2);
        if (left > 2) result.push("...");
        for (let i = left; i <= right; i++) result.push(i);
        if (right < totalPages - 1) result.push("...");
        result.push(totalPages);
        return result;
    }, [currentPage, totalPages]);

    const startRow = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endRow = Math.min(currentPage * pageSize, totalRows);

    return (
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
                Showing <span className="font-semibold text-gray-700">{startRow}–{endRow}</span> of{" "}
                <span className="font-semibold text-gray-700">{totalRows.toLocaleString()}</span> rows
            </p>
            <div className="flex items-center gap-1">
                <NavBtn onClick={() => onPageChange(1)} disabled={currentPage <= 1 || isLoading}>
                    <FontAwesomeIcon icon={faAnglesLeft} className="w-3 h-3" />
                </NavBtn>
                <NavBtn onClick={() => onPageChange(currentPage - 1)} disabled={currentPage <= 1 || isLoading}>
                    <FontAwesomeIcon icon={faChevronLeft} className="w-3 h-3" />
                </NavBtn>
                {pages.map((p, i) =>
                    p === "..." ? (
                        <span key={`e${i}`} className="px-1 text-xs text-gray-400">…</span>
                    ) : (
                        <button key={p} onClick={() => onPageChange(p)} disabled={isLoading}
                            className={`min-w-[32px] h-8 px-2 text-xs font-semibold rounded-lg transition-colors ${p === currentPage ? "bg-[#144677] text-white" : "text-gray-600 hover:bg-gray-100 border border-gray-200"}`}>
                            {p}
                        </button>
                    )
                )}
                <NavBtn onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages || isLoading}>
                    <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
                </NavBtn>
                <NavBtn onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages || isLoading}>
                    <FontAwesomeIcon icon={faAnglesRight} className="w-3 h-3" />
                </NavBtn>
            </div>
            <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500">Rows/page</label>
                <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    className="rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-[#144677]">
                    {PAGE_SIZE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
            </div>
        </div>
    );
}

function NavBtn({ onClick, disabled, children }) {
    return (
        <button onClick={onClick} disabled={disabled}
            className="w-8 h-8 flex items-center justify-center text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            {children}
        </button>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function QbrTableCard({
    fromDate,
    toDate,
    parentCustomer = "",
    childCustomer = "",
    carrier: globalCarrier = "",
    service: globalService = "",
}) {
    // Local (QBR-specific) draft filters
    const [machshipNumber, setMachshipNumber] = useState("");
    const [carrier, setCarrier] = useState("");
    const [customer, setCustomer] = useState("");
    const [managementStatus, setManagementStatus] = useState("");
    const [appliedLocal, setAppliedLocal] = useState({ machshipNumber: "", carrier: "", customer: "", managementStatus: "" });

    // Data
    const [tableData, setTableData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [totalRows, setTotalRows] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    // UI
    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [error, setError] = useState("");
    const [sortCol, setSortCol] = useState(null);
    const [sortDir, setSortDir] = useState("asc");
    const [activePreset, setActivePreset] = useState("Core");
    const [visibleCols, setVisibleCols] = useState(new Set(PRESETS.Core));

    // ── Fetch ────────────────────────────────────────────────────────
    const fetchData = useCallback(async (page, size, local) => {
        if (!fromDate || !toDate) return;
        setIsLoading(true);
        setError("");
        try {
            const p = new URLSearchParams({ from_date: fromDate, to_date: toDate, page: String(page), page_size: String(size) });
            if (local.machshipNumber) p.set("machship_number", local.machshipNumber);
            if (parentCustomer) p.set("parent_customer", parentCustomer);
            if (childCustomer) p.set("child_customer", childCustomer);
            if (local.carrier || globalCarrier) p.set("carrier", local.carrier || globalCarrier);
            if (globalService) p.set("service", globalService);
            if (local.customer || childCustomer) p.set("customer", local.customer || childCustomer);
            if (local.managementStatus) p.set("management_status", local.managementStatus);

            const res = await fetchWithAuth(`/api/v1/qbr/table?${p.toString()}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const json = await res.json();
            if (json.error) throw new Error(json.error);

            setTableData(normalizeRows(json.data));
            setSummary(json.summary || null);
            setTotalRows(json.pagination?.total_rows || 0);
            setTotalPages(json.pagination?.total_pages || 0);
        } catch (err) {
            setError(err.message);
            setTableData([]);
            setSummary(null);
        } finally {
            setIsLoading(false);
        }
    }, [childCustomer, fromDate, globalCarrier, globalService, parentCustomer, toDate]);

    // Auto-fetch on mount / when global filters change
    useEffect(() => {
        setCurrentPage(1);
        fetchData(1, pageSize, appliedLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childCustomer, fromDate, globalCarrier, globalService, parentCustomer, toDate]);

    const handleApplyLocalFilters = () => {
        const next = { machshipNumber, carrier, customer, managementStatus };
        setAppliedLocal(next);
        setCurrentPage(1);
        fetchData(1, pageSize, next);
    };

    const handleClearLocalFilters = () => {
        setMachshipNumber("");
        setCarrier("");
        setCustomer("");
        setManagementStatus("");
        const next = { machshipNumber: "", carrier: "", customer: "", managementStatus: "" };
        setAppliedLocal(next);
        setCurrentPage(1);
        fetchData(1, pageSize, next);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchData(page, pageSize, appliedLocal);
    };

    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setCurrentPage(1);
        fetchData(1, size, appliedLocal);
    };

    // ── Column presets ───────────────────────────────────────────────
    const handlePreset = (name) => {
        setActivePreset(name);
        setVisibleCols(new Set(PRESETS[name]));
    };

    const toggleColumn = (key) => {
        setVisibleCols((prev) => {
            const next = new Set(prev);
            if (next.has(key)) { if (next.size === 1) return prev; next.delete(key); }
            else next.add(key);
            setActivePreset("");
            return next;
        });
    };

    // Ordered visible columns preserving ALL_COLUMNS order
    const orderedVisible = useMemo(() => ALL_COLUMNS.filter((c) => visibleCols.has(c.key)), [visibleCols]);

    // ── Sort ─────────────────────────────────────────────────────────
    const processedRows = useMemo(() => {
        if (!sortCol) return tableData;
        return [...tableData].sort((a, b) => {
            const va = a[sortCol], vb = b[sortCol];
            if (va == null) return 1; if (vb == null) return -1;
            const na = Number(va), nb = Number(vb);
            const cmp = !isNaN(na) && !isNaN(nb) ? na - nb : String(va).localeCompare(String(vb));
            return sortDir === "asc" ? cmp : -cmp;
        });
    }, [tableData, sortCol, sortDir]);

    const handleSort = (key) => {
        if (sortCol === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
        else { setSortCol(key); setSortDir("asc"); }
    };

    // ── Export ───────────────────────────────────────────────────────
    const handleExport = async (visibleOnly) => {
        setIsExporting(true);
        try {
            const p = new URLSearchParams({ from_date: fromDate, to_date: toDate });
            if (appliedLocal.machshipNumber) p.set("machship_number", appliedLocal.machshipNumber);
            if (parentCustomer) p.set("parent_customer", parentCustomer);
            if (childCustomer) p.set("child_customer", childCustomer);
            if (appliedLocal.carrier || globalCarrier) p.set("carrier", appliedLocal.carrier || globalCarrier);
            if (globalService) p.set("service", globalService);
            if (appliedLocal.customer || childCustomer) p.set("customer", appliedLocal.customer || childCustomer);
            if (appliedLocal.managementStatus) p.set("management_status", appliedLocal.managementStatus);
            if (visibleOnly) p.set("columns", [...visibleCols].join(","));

            const res = await fetchWithAuth(`/api/v1/qbr/export?${p.toString()}`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `qbr_${fromDate}_${toDate}.csv`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error("QBR export failed:", err);
        } finally {
            setIsExporting(false);
        }
    };

    const hasLocalFilters = appliedLocal.machshipNumber || appliedLocal.carrier || appliedLocal.customer || appliedLocal.managementStatus;

    return (
        <div className="m-4 overflow-visible rounded-2xl border border-gray-200 bg-white shadow-lg">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#144677] to-[#1a5c99] px-6 py-4 rounded-t-2xl">
                <div className="flex items-center gap-3">
                    <div className="rounded-lg bg-white/10 p-2">
                        <FontAwesomeIcon icon={faTableList} className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-white">QBR Consignment Table</h2>
                        <p className="text-white/70 text-xs mt-0.5">
                            {fromDate} → {toDate}
                            {hasLocalFilters && <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full">Filters active</span>}
                        </p>
                    </div>
                </div>
            </div>

            {/* Additional filters */}
            <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/60">
                <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faSliders} className="w-3.5 h-3.5 text-[#144677]" />
                    <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">Refine Results</span>
                </div>
                <div className="flex flex-wrap gap-3 items-end">
                    <div>
                        <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Machship #</label>
                        <input type="text" placeholder="Partial match…" value={machshipNumber}
                            onChange={(e) => setMachshipNumber(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleApplyLocalFilters()}
                            className="w-40 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#144677] focus:border-[#144677]" />
                    </div>
                    <div>
                        <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Carrier</label>
                        <input type="text" placeholder="Partial match…" value={carrier}
                            onChange={(e) => setCarrier(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleApplyLocalFilters()}
                            className="w-40 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#144677] focus:border-[#144677]" />
                    </div>
                    <div>
                        <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Customer</label>
                        <input type="text" placeholder="Partial match…" value={customer}
                            onChange={(e) => setCustomer(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleApplyLocalFilters()}
                            className="w-40 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#144677] focus:border-[#144677]" />
                    </div>
                    <div>
                        <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Management Status</label>
                        <select value={managementStatus}
                            onChange={(e) => setManagementStatus(e.target.value)}
                            className="w-40 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#144677] focus:border-[#144677] bg-white">
                            <option value="">All</option>
                            <option value="Managed">Managed</option>
                            <option value="Non-Managed">Non-Managed</option>
                        </select>
                    </div>
                    <div className="flex gap-2">
                        {hasLocalFilters && (
                            <button onClick={handleClearLocalFilters}
                                className="px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors">
                                Clear
                            </button>
                        )}
                        <button onClick={handleApplyLocalFilters} disabled={isLoading}
                            className="px-4 py-1.5 text-xs font-bold text-white bg-[#144677] hover:bg-[#1a5c99] rounded-lg transition-colors disabled:opacity-50">
                            {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin w-3 h-3" /> : "Refine"}
                        </button>
                    </div>
                </div>
            </div>

            {/* KPI Strip */}
            {summary && <div className="pt-4"><KPIStrip summary={summary} /></div>}

            {/* Toolbar */}
            <div className="px-5 py-3 border-b border-gray-100 space-y-2.5">
                {/* Preset buttons + col picker */}
                <div className="flex flex-wrap items-center gap-2">
                    {PRESET_NAMES.map((name) => (
                        <button key={name} onClick={() => handlePreset(name)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${activePreset === name ? "bg-[#144677] text-white border-[#144677]" : "text-gray-600 border-gray-200 hover:border-[#144677]/50 hover:text-[#144677]"}`}>
                            {name}
                        </button>
                    ))}
                    <div className="ml-auto">
                        <ColPicker visibleCols={visibleCols} onToggle={toggleColumn} activePreset={activePreset} />
                    </div>
                </div>

                {/* Summary bar + export */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-xs text-gray-400">
                        <span className="font-semibold text-gray-600">{totalRows.toLocaleString()}</span> rows &nbsp;·&nbsp;{" "}
                        <span className="font-semibold text-gray-600">{orderedVisible.length}</span> of {ALL_COLUMNS.length} columns
                        {sortCol && <span className="ml-2 text-amber-600">sorted by {ALL_COLUMNS.find(c => c.key === sortCol)?.label}</span>}
                    </p>
                    <div className="flex gap-2">
                        <button onClick={() => handleExport(true)} disabled={isExporting || isLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-gray-200 rounded-lg text-gray-600 hover:border-[#144677]/50 transition-colors disabled:opacity-40">
                            <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />
                            Visible (.csv)
                        </button>
                        <button onClick={() => handleExport(false)} disabled={isExporting || isLoading}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-[#144677] hover:bg-[#1a5c99] rounded-lg transition-colors disabled:opacity-40">
                            {isExporting ? <FontAwesomeIcon icon={faSpinner} className="animate-spin w-3 h-3" /> : <FontAwesomeIcon icon={faDownload} className="w-3 h-3" />}
                            All (.csv)
                        </button>
                    </div>
                </div>
            </div>

            {/* Table */}
            {isLoading && (
                <div className="flex items-center justify-center py-20">
                    <div className="flex flex-col items-center gap-3">
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#144677] border-t-transparent" />
                        <p className="text-sm text-gray-500">Loading QBR data…</p>
                    </div>
                </div>
            )}

            {!isLoading && error && (
                <div className="mx-5 my-4 rounded-lg border-l-4 border-red-400 bg-red-50 p-4">
                    <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
            )}

            {!isLoading && !error && tableData.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                    <FontAwesomeIcon icon={faTableList} className="h-10 w-10 mb-3 opacity-30" />
                    <p className="text-sm">No data found for the selected filters.</p>
                </div>
            )}

            {!isLoading && !error && tableData.length > 0 && (
                <div className="overflow-x-auto">
                    <div className="max-h-[60vh] overflow-auto">
                        <table className="min-w-full text-left text-xs">
                            <thead className="sticky top-0 z-10">
                                <tr>
                                    {orderedVisible.map((col) => (
                                        <th key={col.key} onClick={() => handleSort(col.key)}
                                            className={`whitespace-nowrap border-b px-4 py-3 font-semibold cursor-pointer select-none transition-colors ${sortCol === col.key ? "bg-[#144677] text-white border-[#1a5c99]" : "bg-[#f0f5fa] text-[#144677] border-[#1a5c99]/30 hover:bg-[#e0ecf8]"}`}>
                                            <div className="flex items-center gap-1.5">
                                                <span>{col.label}</span>
                                                {sortCol === col.key
                                                    ? <FontAwesomeIcon icon={sortDir === "asc" ? faSortUp : faSortDown} className="w-3 h-3 text-white" />
                                                    : <FontAwesomeIcon icon={faSort} className="w-3 h-3 opacity-25" />}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="bg-white">
                                {processedRows.map((row, i) => (
                                    <tr key={i} className="odd:bg-white even:bg-slate-50/50 hover:bg-blue-50/40 transition-colors">
                                        {orderedVisible.map((col) => (
                                            <td key={col.key} className="whitespace-nowrap border-b border-gray-100 px-4 py-2 text-gray-700 max-w-[240px] truncate">
                                                {formatCell(col.key, row[col.key])}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Pagination */}
            {!isLoading && totalPages > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalRows={totalRows}
                    pageSize={pageSize}
                    isLoading={isLoading}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}
        </div>
    );
}
